import { Button, Checkbox } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { AutocompleteField } from "./AutocompleteField";
import { AsyncAutocompleteField } from "./AyncAutocompleteField";
import { DateField } from "./DateField";
import { DateRangeField } from "./DateRangeField";
import { InputField } from "./InputField";
import { SectionAutocompleteField } from "./SectionAutocomplete";
import { SelectField } from "./SelectField";
import { SelectSectionField } from "./SelectSection";
import { useFilterTable, ResetSpec } from "./useFilterTable";

import { FieldValues, Path, PathValue } from "react-hook-form";
import { ZodSchema } from "zod";
import {
  AsyncAutocompleteFieldProps,
  AutocompleteFieldProps,
  Field,
  InputFieldProps,
  SectionAutocompleteFieldProps,
  SectionSelectFieldProps,
  SelectFieldProps,
  DateRangeFieldProps,
} from "./types";

interface FilterFormProps<T extends FieldValues> {
  schema: ZodSchema<T>;
  client: string;
  tableName: string;
  fields: Field[];
  columns?: number;
  customBuildPath?: () => string;
  excludeFieldsFromConvertDate?: string[];
  resetParamsAlways?: ResetSpec;
  resetParamsIfFiltersChanged?: ResetSpec;
  resetParamsOnHandleReset?: ResetSpec;
}

export function FilterTable<T extends FieldValues>({
  schema,
  client,
  tableName,
  fields,
  columns = 1,
  customBuildPath,
  excludeFieldsFromConvertDate,
  resetParamsAlways,
  resetParamsIfFiltersChanged = { page: "1" },
  resetParamsOnHandleReset,
}: FilterFormProps<T>) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    onSubmit,
    handleReset,
  } = useFilterTable<T>({
    client,
    schema,
    tableName,
    customBuildPath,
    excludeFieldsFromConvertDate,
    resetParamsAlways,
    resetParamsIfFiltersChanged,
    resetParamsOnHandleReset,
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="px-1 py-2 w-full min-w-[13.75rem] flex flex-col gap-2"
    >
      <div className={`grid grid-cols-${columns} gap-4`}>
        {fields.map((field) => {
          const span = Math.max(1, Math.min(field.col_span ?? 1, columns));
          const cellStyle: React.CSSProperties = {
            gridColumn: `span ${span} / span ${span}`,
          };

          switch (field.type) {
            case "input":
              return (
                <InputField<T>
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  input_type={(field as InputFieldProps).input_type}
                  allowZero={(field as InputFieldProps).allowZero ?? true}
                  allowNegative={
                    (field as InputFieldProps).allowNegative ?? true
                  }
                />
              );

            case "autocomplete":
              return (
                <AutocompleteField<T>
                  key={field.name}
                  field={field as AutocompleteFieldProps}
                  setValue={setValue as (name: Path<T>, value: any) => void}
                  watch={watch}
                />
              );

            case "sectionAutocomplete":
              return (
                <SectionAutocompleteField<T>
                  key={field.name}
                  field={field as SectionAutocompleteFieldProps}
                  setValue={setValue as (name: Path<T>, value: any) => void}
                  watch={watch}
                />
              );

            case "sectionSelect":
              return (
                <SelectSectionField<T>
                  key={field.name}
                  field={field as SectionSelectFieldProps}
                  setValue={setValue as (name: Path<T>, value: any) => void}
                  watch={watch}
                />
              );

            case "asyncAutocomplete":
              return (
                <div key={field.name} style={cellStyle}>
                  <AsyncAutocompleteField<T>
                    name={field.name}
                    label={field.label}
                    base_url={(field as AsyncAutocompleteFieldProps).base_url}
                    column_name={
                      (field as AsyncAutocompleteFieldProps).column_name
                    }
                    id_column={(field as AsyncAutocompleteFieldProps).id_column}
                    getValues={getValues}
                    setValue={setValue}
                    watch={watch}
                    allowEmptyQuery={
                      (field as AsyncAutocompleteFieldProps).allow_empty_query
                    }
                    minQueryLength={
                      (field as AsyncAutocompleteFieldProps).min_query_length
                    }
                    filters={(field as AsyncAutocompleteFieldProps).filters}
                  />
                </div>
              );

            case "select":
              return (
                <SelectField<T>
                  key={field.name}
                  field={field as SelectFieldProps}
                  setValue={setValue as (name: Path<T>, value: any) => void}
                  watch={watch}
                />
              );

            case "date":
              return (
                <DateField<T>
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  watch={watch}
                  setValue={setValue}
                  register={register}
                />
              );

            case "dateRange": {
              const drField = field as DateRangeFieldProps;
              return (
                <div
                  key={`${drField.from.name}-${drField.to.name}`}
                  style={cellStyle}
                >
                  <DateRangeField<T>
                    label={drField.label}
                    from={drField.from}
                    to={drField.to}
                    stacked={columns <= 1}
                    copyFromToWhenToEmpty={!!drField.copy_from_to_when_to_empty}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                  />
                </div>
              );
            }

            case "checkbox":
              return (
                <div key={field.name} className="py-2">
                  <Checkbox
                    isSelected={watch(field.name as Path<T>)}
                    size="sm"
                    onChange={(event) =>
                      setValue(
                        field.name as Path<T>,
                        event.target.checked as PathValue<T, Path<T>>,
                      )
                    }
                    classNames={{
                      label: "text-blue-600",
                    }}
                  >
                    {field.label}
                  </Checkbox>
                </div>
              );

            default:
              return null;
          }
        })}
      </div>

      <footer className="mt-4 flex gap-2">
        <Button color="primary" radius="full" size="sm" type="submit">
          {t("actions.search")}
        </Button>
        <Button
          color="primary"
          radius="full"
          variant="flat"
          size="sm"
          onPress={handleReset}
        >
          {t("actions.reset")}
        </Button>
      </footer>
    </form>
  );
}
